import React, { useEffect, useState } from 'react';
import { Modal, Input, Button, Checkbox, Spin, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { useFormik } from 'formik';
import { useMutation } from 'react-query';
import rootApi from '@utils/baseAPI/WrapperApi';
import { showError, showSuccess } from '@components/notification';

const AssignCustomerModal = ({ open, onCancel, depot, refetch }) => {
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]); // ✅ đổi sang array
  const [searchText, setSearchText] = useState('');

  // --- API lấy danh sách khách hàng ---
  const fetchCustomers = async (keyword = '') => {
    setLoading(true);
    try {
      const res = await rootApi.get(`/admin/users/customer`, {
        params: {
          search_username: keyword || undefined,
          page: 0,
          size: 20
        }
      });
      setCustomers(res.data?.data || []);
    } catch (err) {
      message.error('Không thể tải danh sách khách hàng');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // --- Debounce tìm kiếm ---
  const handleSearch = debounce((value) => {
    fetchCustomers(value);
  }, 400);

  // --- Khi mở modal ---
  useEffect(() => {
    if (open) {
      fetchCustomers();
      setSelectedCustomers([]);
      setSearchText('');
    }
  }, [open]);

  // --- Toggle chọn khách hàng ---
  const handleSelect = (customer) => {
    setSelectedCustomers((prev) => {
      const exists = prev.find((item) => item.id === customer.id);
      if (exists) {
        return prev.filter((item) => item.id !== customer.id);
      } else {
        return [...prev, customer];
      }
    });
  };

  // --- API gán khách hàng vào trạm ---
  const { mutateAsync, isLoading } = useMutation(['bind-users'], (data) => {
    const user_ids = data.user_ids.map((item) => item?.value);
    return rootApi.post(`/depot-users/bind`, {
      depot_code: depot?.depot_code,
      user_ids
    });
  });

  const formik = useFormik({
    initialValues: {
      user_ids: []
    },
    onSubmit: (values) => {
      mutateAsync(values)
        .then(() => {
          handleClose();
          showSuccess({ message: 'Gán khách hàng vào trạm thành công' });
        })
        .catch((error) => {
          showError(error || 'Vui lòng liên hệ admin để được hỗ trợ');
        });
    }
  });

  const handleClose = () => {
    formik.resetForm();
    refetch?.(); // reload danh sách cha
    onCancel?.();
  };

  // --- Khi bấm nút “Gán khách hàng” ---
  const handleAssign = () => {
    if (!selectedCustomers.length) {
      message.warning('Vui lòng chọn ít nhất 1 khách hàng!');
      return;
    }

    const userIds = selectedCustomers.map((c) => ({ value: c.id }));
    formik.setFieldValue('user_ids', userIds);
    formik.submitForm();
  };

  return (
    <Modal
      title="Gán khách hàng vào trạm sạc"
      open={open}
      onCancel={handleClose}
      footer={null}
      width={600}
      centered>
      {/* --- Ô tìm kiếm --- */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Input
          placeholder="Tìm kiếm theo tên đăng nhập hoặc email"
          prefix={<SearchOutlined />}
          onChange={(e) => {
            setSearchText(e.target.value);
            handleSearch(e.target.value);
          }}
          value={searchText}
          style={{ height: 38 }}
        />
        <Button
          type="primary"
          style={{
            backgroundColor: '#85B79D',
            borderColor: '#85B79D',
            height: 38
          }}
          onClick={() => fetchCustomers(searchText)}>
          Tìm kiếm
        </Button>
      </div>

      {/* --- Danh sách khách hàng --- */}
      <div
        style={{
          maxHeight: 320,
          overflowY: 'auto',
          borderRadius: 6,
          border: '1px solid #f0f0f0'
        }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : (
          customers.map((customer) => {
            const checked = selectedCustomers.some((c) => c.id === customer.id);
            return (
              <div
                key={customer.id}
                onClick={() => handleSelect(customer)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 16px',
                  cursor: 'pointer',
                  backgroundColor: checked ? '#F6FFED' : 'white',
                  borderBottom: '1px solid #f0f0f0'
                }}>
                <Checkbox
                  checked={checked}
                  onChange={() => handleSelect(customer)}
                  style={{ marginRight: 12 }}
                />
                <div>
                  <div
                    style={{
                      color: '#192434',
                      fontSize: 14,
                      fontWeight: 500
                    }}>
                    {customer.username}
                  </div>
                  <div
                    style={{
                      color: '#7A7F85',
                      fontSize: 13,
                      marginTop: 2
                    }}>
                    {customer.phone_number || customer.email}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* --- Footer --- */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 16
        }}>
        <span style={{ color: '#626D7C' }}>Đã chọn {selectedCustomers.length} khách hàng</span>
        <div>
          <Button onClick={handleClose} style={{ marginRight: 8 }}>
            Hủy bỏ
          </Button>
          <Button
            type="primary"
            loading={isLoading}
            style={{
              backgroundColor: '#85B79D',
              borderColor: '#85B79D'
            }}
            onClick={handleAssign}>
            Gán khách hàng
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AssignCustomerModal;
